// Wire types mirroring api-spec/openapi/*.yaml, and tolerant parsers for them.
//
// Field names deliberately match the specification, so comparing code against the
// spec is mechanical rather than a translation exercise. Parsing is tolerant the way
// the reference client's models.dart is: bad wire data degrades (missing string →
// '', unknown element type → the unknown card), it does not throw mid-conversation.

export type AuthorKind = 'buyer' | 'agent' | 'human' | 'system';
export type EntryStatus = 'active' | 'withdrawn' | 'moved' | 'change_pending';
export type OrderState = 'received' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
export type NotificationCategory = 'order_status' | 'reminder' | 'outage' | 'other';

/** Whether anything new may be sent to an entry in this state. */
export const canSendTo = (status: EntryStatus): boolean => status === 'active';

type Rec = Record<string, unknown>;

const isRec = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const strOrU = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const int = (v: unknown, fallback = 0): number => (Number.isInteger(v) ? (v as number) : fallback);
const intOrU = (v: unknown): number | undefined => (Number.isInteger(v) ? (v as number) : undefined);
const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const bool = (v: unknown, fallback = false): boolean => (typeof v === 'boolean' ? v : fallback);

const timeOrU = (v: unknown): Date | undefined => {
  if (typeof v !== 'string') return undefined;
  const ms = Date.parse(v);
  return Number.isNaN(ms) ? undefined : new Date(ms);
};

export function parseAuthorKind(v: unknown): AuthorKind {
  return v === 'buyer' || v === 'agent' || v === 'human' ? v : 'system';
}

export function parseEntryStatus(v: unknown): EntryStatus {
  return v === 'withdrawn' || v === 'moved' || v === 'change_pending' ? v : 'active';
}

export function parseOrderState(v: unknown): OrderState {
  return v === 'processing' || v === 'shipped' || v === 'delivered' || v === 'cancelled'
    ? v
    : 'received';
}

export function parseNotificationCategory(v: unknown): NotificationCategory {
  return v === 'order_status' || v === 'reminder' || v === 'outage' ? v : 'other';
}

// ——————————————————————————————————————————————————————————————
// Money
// ——————————————————————————————————————————————————————————————

/** Amount is the number of smallest currency units — an integer end to end, so a
 *  floating-point rounding error can never appear. */
export interface Money {
  amount: number;
  currency: string;
}

export function parseMoney(v: unknown): Money | undefined {
  if (!isRec(v)) return undefined;
  const amount = v['amount'];
  const currency = v['currency'];
  if (!Number.isInteger(amount) || typeof currency !== 'string') return undefined;
  return { amount: amount as number, currency };
}

// ——————————————————————————————————————————————————————————————
// Message elements
// ——————————————————————————————————————————————————————————————

/** A formatting range given alongside the text rather than inside it. Indexes count
 *  code points. Styles: `bold`, `italic`, `list_item`; unknown styles are skipped. */
export interface StyleRange {
  start: number;
  end: number;
  style: string;
}

export interface TextElement {
  type: 'text';
  text: string;
  formatting: StyleRange[];
}

export interface VoiceElement {
  type: 'voice';
  mediaId: string;
  durationMs: number;
  /** Filled in by the company's server — the client carries no speech model. */
  transcript: string | undefined;
  waveform: number[];
}

export interface ImageElement {
  type: 'image';
  mediaId: string;
  width: number | undefined;
  height: number | undefined;
  caption: string | undefined;
}

export interface QuestionElement {
  type: 'question';
  questionId: string;
  text: string;
  suggestions: string[];
  isRequired: boolean;
}

export interface AnswerElement {
  type: 'answer';
  questionId: string;
  text: string;
}

export interface RequestedItem {
  itemId: string;
  quantity: number;
}

/** What a card's "ask for these items" button sends. Not an order and no prices — the
 *  company answers with a priced cart, which is what keeps this client from ever
 *  stating a price. */
export interface RequestElement {
  type: 'request';
  items: RequestedItem[];
  fallbackText: string | undefined;
}

export interface CartItem {
  itemId: string;
  name: string;
  quantity: number;
  unit: string | undefined;
  /** Prices are filled in by the server only. The client neither sends nor recomputes
   *  them — the seller is the source of truth on price. */
  unitPrice: Money | undefined;
  total: Money | undefined;
  note: string | undefined;
}

export interface CartElement {
  type: 'cart';
  cartId: string;
  items: CartItem[];
  /** `proposed`, `accepted`, `declined`, `amended`, `superseded` — as on the wire. */
  state: string;
  total: Money | undefined;
}

export interface OrderElement {
  type: 'order';
  orderId: string;
  state: OrderState;
  distributorReference: string | undefined;
  total: Money | undefined;
  items: CartItem[];
  expectedDelivery: Date | undefined;
}

export interface NotificationElement {
  type: 'notification';
  category: NotificationCategory;
  text: string;
  orderId: string | undefined;
}

/** RESERVED — the client shows an informational card with no way to pay. */
export interface PaymentRequestElement {
  type: 'payment_request';
  requestId: string;
  money: Money;
  orderId: string | undefined;
  /** `pending`, `paid`, `expired` or `cancelled`. */
  state: string;
}

export interface LocationElement {
  type: 'location';
  name: string | undefined;
  address: string | undefined;
  latitude: number | undefined;
  longitude: number | undefined;
  fallbackText: string | undefined;
}

/** Two elements with the same `event_id` in one thread describe the same event —
 *  the occurrence with the higher sequence replaces the older one wholesale, and
 *  `status: cancelled` calls the event off without deleting it from history. */
export interface CalendarEventElement {
  type: 'calendar_event';
  eventId: string;
  title: string;
  startsAt: Date;
  endsAt: Date | undefined;
  place: string | undefined;
  note: string | undefined;
  /** `confirmed` or `cancelled`. Absent on the wire means `confirmed`. */
  status: string;
  fallbackText: string | undefined;
}

export interface VideoElement {
  type: 'video';
  mediaId: string;
  durationMs: number | undefined;
  caption: string | undefined;
}

/** A control kind. Closed on purpose: the card says WHERE a control goes and never
 *  what it does, so a company cannot name an action this app does not already perform.
 *  Adding one takes a release of this app, not a different payload. */
export type ControlKind =
  | 'quantity'
  | 'request_items'
  | 'cart_submit'
  | 'answer'
  | 'calendar_add';

export interface CardControl {
  kind: ControlKind;
  itemId: string | undefined;
  cartId: string | undefined;
  questionId: string | undefined;
  eventId: string | undefined;
}

export interface HtmlElementCard {
  type: 'html';
  html: string;
  /** Fills the numbered `<ctl i="N">` slots in `html`. Captions are never taken from
   *  here — this app draws its own wording per kind. */
  controls: CardControl[];
  fallbackText: string | undefined;
}

/** An element type this client version does not know. The spec requires showing
 *  `fallback_text` when present, and a fixed placeholder otherwise — never dropping
 *  the element silently. */
export interface UnknownElement {
  type: 'unknown';
  rawType: string;
  fallbackText: string | undefined;
}

export type MessageElement =
  | TextElement
  | VoiceElement
  | ImageElement
  | QuestionElement
  | AnswerElement
  | RequestElement
  | CartElement
  | OrderElement
  | NotificationElement
  | PaymentRequestElement
  | LocationElement
  | CalendarEventElement
  | VideoElement
  | HtmlElementCard
  | UnknownElement;

function parseStyleRange(v: unknown): StyleRange | undefined {
  if (!isRec(v)) return undefined;
  const start = v['start'];
  const end = v['end'];
  const style = v['style'];
  if (!Number.isInteger(start) || !Number.isInteger(end) || typeof style !== 'string') {
    return undefined;
  }
  return { start: start as number, end: end as number, style };
}

function parseCartItem(v: Rec): CartItem {
  return {
    itemId: str(v['item_id']),
    name: str(v['name']),
    quantity: num(v['quantity']),
    unit: strOrU(v['unit']),
    unitPrice: parseMoney(v['unit_price']),
    total: parseMoney(v['total']),
    note: strOrU(v['note']),
  };
}

function recArray(v: unknown): Rec[] {
  return Array.isArray(v) ? v.filter(isRec) : [];
}

const CONTROL_KINDS: ReadonlySet<string> = new Set([
  'quantity',
  'request_items',
  'cart_submit',
  'answer',
  'calendar_add',
]);

/** An unknown kind is dropped rather than rendered as something else. The server rejects
 *  one already; this is the second half of not trusting the other side, and dropping is
 *  the safe direction — a missing button is a nuisance, a guessed one is a hazard. */
export function parseControls(v: unknown): CardControl[] {
  if (!Array.isArray(v)) {
    return [];
  }
  const out: CardControl[] = [];
  for (const entry of v) {
    if (!isRec(entry)) {
      continue;
    }
    const kind = entry['kind'];
    if (typeof kind !== 'string' || !CONTROL_KINDS.has(kind)) {
      continue;
    }
    out.push({
      kind: kind as ControlKind,
      itemId: strOrU(entry['item_id']),
      cartId: strOrU(entry['cart_id']),
      questionId: strOrU(entry['question_id']),
      eventId: strOrU(entry['event_id']),
    });
  }
  return out;
}

export function parseElement(json: unknown): MessageElement {
  if (!isRec(json)) {
    return { type: 'unknown', rawType: 'unknown', fallbackText: undefined };
  }
  const t = json['type'];
  switch (t) {
    case 'text':
      return {
        type: 'text',
        text: str(json['text']),
        formatting: Array.isArray(json['formatting'])
          ? json['formatting'].map(parseStyleRange).filter((r): r is StyleRange => r !== undefined)
          : [],
      };
    case 'voice':
      return {
        type: 'voice',
        mediaId: str(json['media_id']),
        durationMs: int(json['duration_ms']),
        transcript: strOrU(json['transcript']),
        waveform: Array.isArray(json['waveform'])
          ? json['waveform'].filter((n): n is number => Number.isInteger(n))
          : [],
      };
    case 'image':
      return {
        type: 'image',
        mediaId: str(json['media_id']),
        width: intOrU(json['width']),
        height: intOrU(json['height']),
        caption: strOrU(json['caption']),
      };
    case 'question':
      return {
        type: 'question',
        questionId: str(json['question_id']),
        text: str(json['text']),
        suggestions: Array.isArray(json['suggestions'])
          ? json['suggestions'].filter((s): s is string => typeof s === 'string')
          : [],
        isRequired: bool(json['required']),
      };
    case 'answer':
      return { type: 'answer', questionId: str(json['question_id']), text: str(json['text']) };
    case 'request':
      return {
        type: 'request',
        items: (Array.isArray(json['items']) ? json['items'] : [])
          .filter(isRec)
          .map((row) => ({ itemId: str(row['item_id']), quantity: int(row['quantity']) }))
          .filter((row) => row.itemId.length > 0 && row.quantity > 0),
        fallbackText: strOrU(json['fallback_text']),
      };
    case 'cart':
      return {
        type: 'cart',
        cartId: str(json['cart_id']),
        items: recArray(json['items']).map(parseCartItem),
        state: str(json['status'], 'proposed'),
        total: parseMoney(json['total']),
      };
    case 'order':
      return {
        type: 'order',
        orderId: str(json['order_id']),
        state: parseOrderState(json['status']),
        distributorReference: strOrU(json['distributor_reference']),
        total: parseMoney(json['total']),
        items: recArray(json['items']).map(parseCartItem),
        expectedDelivery: timeOrU(json['expected_delivery']),
      };
    case 'notification':
      return {
        type: 'notification',
        category: parseNotificationCategory(json['category']),
        text: str(json['text']),
        orderId: strOrU(json['order_id']),
      };
    case 'payment_request':
      return {
        type: 'payment_request',
        requestId: str(json['request_id']),
        money: parseMoney(json['amount']) ?? { amount: 0, currency: 'PLN' },
        orderId: strOrU(json['order_id']),
        state: str(json['status'], 'pending'),
      };
    case 'location': {
      const lat = json['latitude'];
      const lon = json['longitude'];
      return {
        type: 'location',
        name: strOrU(json['name']),
        address: strOrU(json['address']),
        latitude: typeof lat === 'number' && Number.isFinite(lat) ? lat : undefined,
        longitude: typeof lon === 'number' && Number.isFinite(lon) ? lon : undefined,
        fallbackText: strOrU(json['fallback_text']),
      };
    }
    case 'calendar_event': {
      // A calendar event without a start date cannot go on any calendar, so a
      // malformed one degrades to the unknown-type card instead of a blank tile.
      const startsAt = timeOrU(json['starts_at']);
      if (startsAt === undefined) {
        return { type: 'unknown', rawType: 'calendar_event', fallbackText: strOrU(json['fallback_text']) };
      }
      return {
        type: 'calendar_event',
        eventId: str(json['event_id']),
        title: str(json['title']),
        startsAt,
        endsAt: timeOrU(json['ends_at']),
        place: strOrU(json['place']),
        note: strOrU(json['note']),
        status: str(json['status'], 'confirmed'),
        fallbackText: strOrU(json['fallback_text']),
      };
    }
    case 'video':
      return {
        type: 'video',
        mediaId: str(json['media_id']),
        durationMs: intOrU(json['duration_ms']),
        caption: strOrU(json['caption']),
      };
    case 'html':
      return {
        type: 'html',
        html: str(json['html']),
        controls: parseControls(json['controls']),
        fallbackText: strOrU(json['fallback_text']),
      };
    default:
      return {
        type: 'unknown',
        rawType: typeof t === 'string' && t.length > 0 ? t : 'unknown',
        fallbackText: strOrU(json['fallback_text']),
      };
  }
}

export function parseElements(json: unknown): MessageElement[] {
  return Array.isArray(json) ? json.map(parseElement) : [];
}

// ——————————————————————————————————————————————————————————————
// Standing pages a company publishes for this buyer
// ——————————————————————————————————————————————————————————————

export interface ViewDescriptor {
  viewId: string;
  title: string;
  version: number;
}

export interface CompanyView {
  viewId: string;
  title: string;
  version: number;
  elements: MessageElement[];
  /** The `elements` array exactly as served, for verbatim storage — the same contract
   *  `EventPage.rawEvents` follows, and for the same reason: what this version cannot
   *  render, a later one still can. */
  rawElements: unknown;
}

export function parseViewDescriptor(json: Rec): ViewDescriptor {
  return { viewId: str(json['view_id']), title: str(json['title']), version: int(json['version']) };
}

export function parseCompanyView(json: Rec): CompanyView {
  return {
    viewId: str(json['view_id']),
    title: str(json['title']),
    version: int(json['version']),
    elements: parseElements(json['elements']),
    rawElements: json['elements'],
  };
}

/** At most six, enforced here as well as on the server. The number is a limit on how much
 *  a company may put in front of a buyer at once, so the client is the side that has to
 *  hold it — a server that returns more is not trusted to have meant it. */
export function parseViewList(json: Rec): ViewDescriptor[] {
  return recArray(json['views']).slice(0, 6).map(parseViewDescriptor);
}

// ——— wire builders for what this client sends ———

export function textElementJson(text: string): Rec {
  return { type: 'text', text };
}

/** Zero quantities are dropped rather than sent as zeroes: "not this one" is said by
 *  leaving an item out, so a request never carries rows that mean nothing. */
export function requestElementJson(items: RequestedItem[]): Rec {
  return {
    type: 'request',
    items: items
      .filter((item) => item.quantity > 0)
      .map((item) => ({ item_id: item.itemId, quantity: item.quantity })),
  };
}

export function answerElementJson(questionId: string, text: string): Rec {
  return { type: 'answer', question_id: questionId, text };
}

export function imageElementJson(input: {
  mediaId: string;
  width?: number;
  height?: number;
  caption?: string;
}): Rec {
  return {
    type: 'image',
    media_id: input.mediaId,
    ...(input.width !== undefined ? { width: input.width } : {}),
    ...(input.height !== undefined ? { height: input.height } : {}),
    ...(input.caption !== undefined && input.caption.length > 0 ? { caption: input.caption } : {}),
  };
}

export function voiceElementJson(input: {
  mediaId: string;
  durationMs: number;
  waveform?: number[];
}): Rec {
  return {
    type: 'voice',
    media_id: input.mediaId,
    duration_ms: input.durationMs,
    ...(input.waveform !== undefined && input.waveform.length > 0
      ? { waveform: input.waveform }
      : {}),
  };
}

// ——————————————————————————————————————————————————————————————
// Events and thread state
// ——————————————————————————————————————————————————————————————

export interface ImpEvent {
  id: string;
  threadId: string;
  sequence: number;
  time: Date;
  authorKind: AuthorKind;
  authorName: string | undefined;
  clientUuid: string | undefined;
  elements: MessageElement[];
  /** The elements exactly as served, for verbatim storage. */
  rawElements: unknown;
}

export function parseEvent(json: Rec): ImpEvent {
  const author = isRec(json['author']) ? (json['author'] as Rec) : {};
  return {
    id: str(json['id']),
    threadId: str(json['thread_id']),
    sequence: int(json['sequence']),
    time: timeOrU(json['time']) ?? new Date(),
    authorKind: parseAuthorKind(author['kind']),
    authorName: strOrU(author['name']),
    clientUuid: strOrU(json['client_uuid']),
    elements: parseElements(json['elements']),
    rawElements: json['elements'] ?? [],
  };
}

/**
 * What the company's side is doing at this instant. Deliberately not an event and
 * deliberately not stored: it has no sequence, never enters history, and a client
 * that missed one has missed nothing. It exists so that a reply which takes seconds
 * does not look like a reply that is not coming.
 */
export interface ThreadActivity {
  note: string;
  /** undefined means "working, length unknown" — show an unbounded indicator. */
  progress: number | undefined;
  since: Date;
  /** When to stop believing it — the indicator lapses on its own. */
  expires: Date;
}

export function parseThreadActivity(v: unknown): ThreadActivity | undefined {
  if (!isRec(v)) return undefined;
  const note = v['note'];
  const since = timeOrU(v['since']);
  const expires = timeOrU(v['expires']);
  if (typeof note !== 'string' || note.length === 0 || since === undefined || expires === undefined) {
    return undefined;
  }
  const progress = v['progress'];
  return {
    note,
    progress:
      Number.isInteger(progress) && (progress as number) >= 0 && (progress as number) <= 100
        ? (progress as number)
        : undefined,
    since,
    expires,
  };
}

export interface ThreadState {
  threadId: string;
  lastSequence: number;
  readOnly: boolean;
  answeredBy: string | undefined;
  activeCart: string | undefined;
  /** Rises whenever a page published to this buyer changes — the only cue to refetch
   *  the page list. A server that does not know pages omits it, and 0 means "nothing
   *  to fetch", which is the right answer for such a server. */
  viewsVersion: number;
  activity: ThreadActivity | undefined;
}

export function parseThreadState(json: Rec): ThreadState {
  return {
    threadId: str(json['thread_id']),
    lastSequence: int(json['last_sequence']),
    readOnly: bool(json['read_only']),
    answeredBy: strOrU(json['answered_by']),
    activeCart: strOrU(json['active_cart']),
    viewsVersion: int(json['views_version']),
    activity: parseThreadActivity(json['activity']),
  };
}

export interface EventPage {
  events: ImpEvent[];
  nextCursor: string | undefined;
  hasMore: boolean;
  threadState: ThreadState[];
  /** Raw event objects, index-aligned with `events`, for verbatim storage. */
  rawEvents: Rec[];
}

export function parseEventPage(json: Rec): EventPage {
  const rawEvents = recArray(json['events']);
  return {
    events: rawEvents.map(parseEvent),
    nextCursor: strOrU(json['next_cursor']),
    hasMore: bool(json['has_more']),
    threadState: recArray(json['thread_state']).map(parseThreadState),
    rawEvents,
  };
}

// ——————————————————————————————————————————————————————————————
// Sessions
// ——————————————————————————————————————————————————————————————

export interface QuarantineInfo {
  active: boolean;
  until: Date | undefined;
  restrictions: string[];
}

export interface SessionResponse {
  token: string;
  expiresAt: Date | undefined;
  threadId: string | undefined;
  accountId: string | undefined;
  distributorName: string | undefined;
  quarantine: QuarantineInfo | undefined;
}

export function parseSessionResponse(json: Rec): SessionResponse {
  const distributor = isRec(json['distributor']) ? (json['distributor'] as Rec) : {};
  const quarantine = isRec(json['quarantine']) ? (json['quarantine'] as Rec) : undefined;
  return {
    token: str(json['token']),
    expiresAt: timeOrU(json['expires_at']),
    threadId: strOrU(json['thread_id']),
    accountId: strOrU(json['account_id']),
    distributorName: strOrU(distributor['name']),
    quarantine:
      quarantine === undefined
        ? undefined
        : {
            active: bool(quarantine['active']),
            until: timeOrU(quarantine['until']),
            restrictions: Array.isArray(quarantine['restrictions'])
              ? quarantine['restrictions'].filter((s): s is string => typeof s === 'string')
              : [],
          },
  };
}

// ——————————————————————————————————————————————————————————————
// Directory
// ——————————————————————————————————————————————————————————————

export interface DirectoryEntry {
  /** slow_hash(salt ‖ E.164), base64url — how an entry is named in the published file.
   *  The number itself does not appear there. */
  entryKey: string;
  accountId: string;
  serverUrl: string;
  serverKey: string;
  status: EntryStatus;
  /** The number, when this client happens to know it — because somebody typed it and
   *  it hashed to `entryKey`. Never read from the published file. */
  phone: string | undefined;
  successorKey: string | undefined;
  successorAccountId: string | undefined;
  /** A move with the predecessor's signature proves continuity — the client follows
   *  the successor on its own. Without it the move waits out the pending period. */
  successorSigned: boolean;
  changePendingUntil: Date | undefined;
  minProtocolVersion: number | undefined;
  maxProtocolVersion: number | undefined;
  /** The entry exactly as served — proof leaves hash over its canonical form. */
  raw: Rec;
}

export function entrySupportsProtocol(entry: DirectoryEntry, version: number): boolean {
  return (
    (entry.minProtocolVersion === undefined || version >= entry.minProtocolVersion) &&
    (entry.maxProtocolVersion === undefined || version <= entry.maxProtocolVersion)
  );
}

export function parseDirectoryEntry(json: Rec, phone?: string): DirectoryEntry | undefined {
  const entryKey = json['key'];
  const accountId = json['account_id'];
  const url = json['server_url'];
  if (typeof entryKey !== 'string' || typeof accountId !== 'string' || typeof url !== 'string') {
    return undefined;
  }
  const rawKey = json['server_key'];
  const serverKey = isRec(rawKey) ? str(rawKey['key']) : str(rawKey);
  const successor = isRec(json['successor']) ? (json['successor'] as Rec) : undefined;
  const versions = isRec(json['server_protocol_version'])
    ? (json['server_protocol_version'] as Rec)
    : undefined;
  return {
    entryKey,
    accountId,
    serverUrl: url,
    serverKey,
    status: parseEntryStatus(json['status']),
    phone: phone ?? strOrU(json['phone']),
    successorKey: successor === undefined ? undefined : strOrU(successor['key']),
    successorAccountId: successor === undefined ? undefined : strOrU(successor['account_id']),
    successorSigned: successor !== undefined && successor['predecessor_signature'] != null,
    changePendingUntil: timeOrU(json['change_pending_until']),
    minProtocolVersion: versions === undefined ? undefined : intOrU(versions['min']),
    maxProtocolVersion: versions === undefined ? undefined : intOrU(versions['max']),
    raw: json,
  };
}

export interface LookupParams {
  algorithm: string;
  saltB64: string;
  cost: Record<string, number>;
  keyBytes: number;
}

export function parseLookupParams(v: unknown): LookupParams | undefined {
  if (!isRec(v)) return undefined;
  const algorithm = v['algorithm'];
  const salt = v['salt'];
  if (typeof algorithm !== 'string' || typeof salt !== 'string') return undefined;
  const cost: Record<string, number> = {};
  if (isRec(v['cost'])) {
    for (const [key, value] of Object.entries(v['cost'] as Rec)) {
      if (Number.isInteger(value)) cost[key] = value as number;
    }
  }
  return { algorithm, saltB64: salt, cost, keyBytes: int(v['key_bytes'], 32) };
}

export interface GatewayEntry {
  id: string;
  url: string;
  keys: string[];
  /** The gateway's VAPID public key. Absent means it serves no webpush, and a
   *  client MUST NOT produce a webpush capability for it. */
  vapidKey: string | undefined;
}

/** The verified body of the directory manifest. */
export interface ManifestContent {
  protocolVersion: number;
  version: number;
  issuedAt: Date;
  validUntil: Date;
  clockToleranceS: number | undefined;
  directoryDomains: string[];
  /** Keys allowed to sign, indexed by fingerprint. */
  keys: Record<string, string>;
  futureKeys: Record<string, string>;
  threshold: number;
  gateways: GatewayEntry[];
  lookup: LookupParams | undefined;
}

export function parseManifestContent(json: Rec): ManifestContent | undefined {
  const version = json['version'];
  const issuedAt = timeOrU(json['issued_at']);
  const validUntil = timeOrU(json['valid_until']);
  if (!Number.isInteger(version) || issuedAt === undefined || validUntil === undefined) {
    return undefined;
  }
  const keys = isRec(json['keys']) ? (json['keys'] as Rec) : {};
  const collect = (field: string): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const item of recArray(keys[field])) {
      out[str(item['id'])] = str(item['key']);
    }
    return out;
  };
  return {
    protocolVersion: int(json['protocol_version']),
    version: version as number,
    issuedAt,
    validUntil,
    clockToleranceS: intOrU(json['clock_tolerance_s']),
    directoryDomains: Array.isArray(json['directory_domains'])
      ? json['directory_domains'].filter((s): s is string => typeof s === 'string')
      : [],
    keys: collect('operational'),
    futureKeys: collect('future'),
    threshold: int(keys['threshold'], 1),
    gateways: recArray(json['gateways']).map((g) => ({
      id: str(g['id']),
      url: str(g['url']),
      keys: recArray(g['keys'])
        .map((k) => str(k['key']))
        .filter((s) => s.length > 0),
      vapidKey: isRec(g['vapid_key']) && typeof (g['vapid_key'] as Rec)['key'] === 'string'
        ? ((g['vapid_key'] as Rec)['key'] as string)
        : undefined,
    })),
    lookup: parseLookupParams(json['lookup']),
  };
}

/** The response to an entry lookup: the entry plus proof that it belongs to the
 *  signed entry set at the stated version. */
export interface EntryLookupResult {
  entry: DirectoryEntry;
  snapshotVersion: number;
  proofRootB64: string;
  proofPathB64: string[];
  signatures: unknown;
  rawEntry: Rec;
}

export function parseEntryLookupResult(json: unknown): EntryLookupResult | undefined {
  if (!isRec(json)) return undefined;
  const rawEntry = json['entry'];
  const version = json['snapshot_version'];
  const proof = json['proof'];
  if (!isRec(rawEntry) || !Number.isInteger(version) || !isRec(proof)) return undefined;
  const entry = parseDirectoryEntry(rawEntry);
  if (entry === undefined) return undefined;
  return {
    entry,
    snapshotVersion: version as number,
    proofRootB64: str(proof['root']),
    proofPathB64: Array.isArray(proof['path'])
      ? proof['path'].filter((s): s is string => typeof s === 'string')
      : [],
    signatures: json['signatures'] ?? [],
    rawEntry,
  };
}
